/**
 * Production S012 loaded conversation-list view and S013 empty-state view.
 *
 * Figma authority:
 * - Loaded:  R4VYIdXEv7HYIXtpDMEvHY:38:2717 (S012 Chat — Conversation List)
 * - Empty:   R4VYIdXEv7HYIXtpDMEvHY:38:2884 (S013 Chat — Empty State)
 * - Empty icon: 38:2909 (32×32, viewBox 0 0 32 32, stroke #3EC9B0)
 *
 * The empty branch is a state completion, not a new feature: it reuses
 * `status: 'empty'` from the model, the Layout-owned mobile shell, and the
 * shared BottomNav. The empty state is exposed as `role="status"` because
 * it appears as the result of an asynchronous load without moving focus.
 * Raw error copy and S012 generic placeholder text are never rendered.
 */
import { Link } from 'react-router-dom';
import { buildChatChannelPath, RoutePaths } from '@/routes';
import { formatConversationTime } from './formatConversationTime';
import type { ChatListModel, ConversationSummary } from './model';
import './ChatList.css';

export interface ChatListViewProps {
  readonly model: ChatListModel;
  readonly referenceTime: string;
}

function Avatar({ conversation }: { readonly conversation: ConversationSummary }) {
  const { participant } = conversation;
  return (
    <span className="chat-list-avatar-wrap">
      <span
        className="chat-list-avatar"
        style={{ backgroundColor: participant.avatar.backgroundColor }}
        aria-hidden="true"
      >
        {participant.avatar.initials}
      </span>
      {participant.isOnline && (
        <span
          className="chat-list-online-indicator"
          aria-label="В сети"
          data-testid={`online-indicator-${conversation.channelId}`}
        />
      )}
    </span>
  );
}

export function ConversationCard({
  conversation,
  referenceTime,
}: {
  readonly conversation: ConversationSummary;
  readonly referenceTime: string;
}) {
  const { participant } = conversation;
  const time = formatConversationTime(
    conversation.lastActivityAt,
    referenceTime,
  );
  const hasUnread = conversation.unreadCount > 0;

  return (
    <Link
      to={buildChatChannelPath(conversation.channelId)}
      className={`conversation-card${hasUnread ? ' conversation-card-unread' : ''}`}
      data-testid={`conversation-card-${conversation.channelId}`}
      aria-label={`Чат с ${participant.displayName}, ${participant.age}, ${participant.city}`}
    >
      <Avatar conversation={conversation} />
      <span className="conversation-card-content">
        <span className="conversation-card-topline">
          <span className="conversation-card-name">
            {participant.displayName}, {participant.age}
          </span>
          <span className="conversation-card-time">{time}</span>
        </span>
        <span className="conversation-card-city">{participant.city}</span>
        <span className="conversation-card-preview-row">
          <span className="conversation-card-preview">{conversation.lastMessage}</span>
          {hasUnread && (
            <span
              className="conversation-card-unread-badge"
              aria-label={`${conversation.unreadCount} непрочитанных`}
              data-testid={`unread-badge-${conversation.channelId}`}
            >
              {conversation.unreadCount}
            </span>
          )}
        </span>
      </span>
    </Link>
  );
}

function LoadingState() {
  return (
    <div className="chat-list-state" role="status" aria-live="polite" data-testid="chat-list-loading">
      <span className="chat-list-state-title">Загрузка чатов…</span>
    </div>
  );
}

/**
 * S013 decorative chat-bubble icon.
 *
 * Figma source node 38:2909. The 32×32 viewBox contains one rounded
 * speech-bubble outline and two horizontal text lines, stroked in
 * `#3EC9B0`. The icon is purely visual; the adjacent heading carries
 * the accessible name, so it is hidden from assistive technologies.
 */
function EmptyChatIcon() {
  return (
    <svg
      viewBox="0 0 32 32"
      className="chat-list-empty-icon"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4 6C4 4.895 4.895 4 6 4H26C27.105 4 28 4.895 28 6V22C28 23.105 27.105 24 26 24H18L13 29V24H6C4.895 24 4 23.105 4 22V6Z"
        fill="none"
        stroke="#3EC9B0"
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      <path
        d="M10 12H22M10 17H18"
        fill="none"
        stroke="#3EC9B0"
        strokeWidth={1.5}
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * S013 canonical empty state. See Figma 38:2884.
 *
 * Layout (top → bottom):
 * - 72×72 circular teal-tinted icon wrap with the empty chat-bubble icon
 * - "У вас пока нет сообщений" heading
 * - "Начните знакомство в разделе «Поиск»." body copy
 * - "Перейти в поиск" primary CTA, semantic link to `/discover`
 *
 * Accessibility:
 * - The region uses `role="status"` because the empty result appears as a
 *   non-focus-moving update after the asynchronous load completes.
 * - The icon is `aria-hidden`; the heading carries the accessible name.
 * - The CTA is a React Router `Link` rendering an anchor; no nested
 *   interactive elements, no imperative click handler, no `window.location`.
 */
function EmptyState() {
  return (
    <section
      className="chat-list-empty"
      role="status"
      aria-labelledby="chat-list-empty-title"
      aria-describedby="chat-list-empty-description"
      data-testid="chat-list-empty"
    >
      <span
        className="chat-list-empty-icon-wrap"
        data-testid="chat-list-empty-icon-wrap"
      >
        <EmptyChatIcon />
      </span>
      <h2
        id="chat-list-empty-title"
        className="chat-list-empty-title"
        data-testid="chat-list-empty-title"
      >
        У вас пока нет сообщений
      </h2>
      <p
        id="chat-list-empty-description"
        className="chat-list-empty-description"
        data-testid="chat-list-empty-description"
      >
        Начните знакомство в разделе «Поиск».
      </p>
      <Link
        to={RoutePaths.DISCOVER}
        className="chat-list-empty-cta"
        data-testid="chat-list-empty-cta"
      >
        Перейти в поиск
      </Link>
    </section>
  );
}

function InactiveSponsorState() {
  return (
    <div className="chat-list-state" data-testid="chat-list-inactive-sponsor">
      <span className="chat-list-state-title">Доступ к чатам временно ограничен.</span>
    </div>
  );
}

function ErrorState() {
  return (
    <div className="chat-list-state" role="alert" data-testid="chat-list-error">
      <span className="chat-list-state-title">Не удалось загрузить чаты. Попробуйте позже.</span>
    </div>
  );
}

export function ChatListView({ model, referenceTime }: ChatListViewProps) {
  return (
    <section className="chat-list-screen" data-testid="chat-list-screen" aria-labelledby="chat-list-heading">
      <header className="chat-list-header">
        <h1 id="chat-list-heading">Чат</h1>
      </header>
      <div className="chat-list-content">
        {model.status === 'loading' && <LoadingState />}
        {model.status === 'empty' && <EmptyState />}
        {model.status === 'inactive_sponsor' && <InactiveSponsorState />}
        {model.status === 'error' && <ErrorState />}
        {model.status === 'ready' && (
          <div className="conversation-card-list" data-testid="conversation-card-list">
            {model.conversations.map((conversation) => (
              <ConversationCard
                key={conversation.channelId}
                conversation={conversation}
                referenceTime={referenceTime}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
